import { LanguageEntry, LanguageEntryWithLocalEmergency, SessionEntryLoading } from "./components/LanguageEntry";
import { LocalEmergencyTile } from "./components/LocalEmergencyTile";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useReducer,
  useMemo,
  type ReactNode,
} from "react";
import { Start, StartWithLocalEmergency, type SelfServiceFlow } from "./Start";
import { CheckInFlow } from "./flows/check-in/CheckInFlow";
import { EmergencyFlow } from "./flows/emergency/EmergencyFlow";
import { PrescriptionFlow } from "./flows/prescription/PrescriptionFlow";
import type { Insurance } from "./flows/scheduler/machine";
import { SchedulerFlow } from "./flows/scheduler/SchedulerFlow";
import {
  SelfServiceProvider,
  useSelfService,
  type PublishedStepAction,
  type StepProgress,
  type ViewTitle,
  type SelfServiceExternalInput,
} from "./context";
import { StepAnnouncer } from "./components/StepAnnouncer";
import { StepFooterActions } from "./components/StepFooterActions";
import { StepProgressIndicator } from "./components/StepProgressIndicator";
import {
  AccessibilitySessionProvider,
  useAccessibilitySession,
} from "./accessibility-session";
import { ResetReassuranceOverlay } from "./components/ResetReassuranceOverlay";
import { ReadabilityToggle } from "./components/ReadabilityToggle";
import { ScrollContainer } from "../../components/scroll-container";
import { AudioGuide } from "../../components/audio-guide";
import { AudioReplayButton } from "../../components/audio-replay-button";
import { LanguageSelector } from "../../components/language-selector";
import { PinVerificationDialog } from "../../components/pin-verification-dialog";
import {
  BridgeOfflineBanner,
  BridgeOfflineBannerWithLocalEmergency,
} from "./components/BridgeOfflineBanner";
import { KioskClock } from "./components/KioskClock";
import { Button } from "@ascleo/ui/components/ui/button";
import { Settings, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../platform/defaults";
import { usePlatform } from "../../platform/context";
import { useOptionalSettingsUnlock } from "../settings/settings-unlock-context";
import { useBridgeConnectivity } from "../../lib/rpc/use-bridge-connectivity";
import {
  KioskPolicyProvider,
  useKioskPolicy,
} from "../../lib/kiosk-policy/kiosk-policy-context";
import { isLocalEmergencyAvailable } from "../../lib/kiosk-policy/kiosk-policy-state";
import { frontdeskDiagnosticLogger } from "../../lib/diagnostic-logger";
import { refreshEebAvailability } from "../../lib/eeb-availability";
import { reportOperationalError } from "../../lib/operational-error-telemetry";
import { ReadabilityProvider } from "../../lib/readability-context";
import { cn } from "@ascleo/ui/lib/utils";
import {
  resolveSelfServiceFlowAvailability,
  type SelfServiceReadinessOverride,
} from "./self-service-readiness";
import {
  DEFAULT_ACTIVE_BRANDING,
  PracticeBrandScope,
  PracticeIdentity,
  useOptionalBranding,
} from "../../branding";
import type { ActiveBrandingSnapshot } from "../../branding";

export interface SelfServicePageProps {
  /**
   * Callback when user wants to navigate to settings (after PIN verification)
   */
  onNavigateToSettings?: () => void;
  /**
   * Whether the shared self-service surface should verify the settings PIN
   * before navigating away. Browser-hosted flows can defer the PIN gate to
   * the destination route itself.
   */
  requirePinVerificationForSettings?: boolean;
  /**
   * Optional custom header actions. When omitted, the default accessibility,
   * audio, language, and settings controls are rendered.
   */
  headerActions?: ReactNode;
  /**
   * Optional host-owned readiness override. When supplied, hosted environments
   * can separate general API reachability from TI/card-sensitive capability
   * without changing the default bridge-backed behavior for other hosts.
   */
  readinessOverride?: SelfServiceReadinessOverride | null;
  /**
   * Optional host observer invoked once when a real self-service flow begins.
   * Observer failures are isolated from the patient journey.
   */
  onFlowStart?: (flow: SelfServiceFlow) => void | Promise<void>;
  /**
   * Optional host observer for the current product-level hub, flow, and
   * external-input requirement. Observer failures are isolated.
   */
  onExperienceContextChange?: (
    context: SelfServiceExperienceContext,
  ) => void | Promise<void>;
}

/**
 * Product-level self-service context exposed to an optional host observer.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const context: SelfServiceExperienceContext = {
 *   kind: "flow",
 *   flow: "checkin",
 *   externalInput: "health-card",
 * };
 * ```
 */
export type SelfServiceExperienceContext =
  | { readonly kind: "hub" }
  | {
      readonly kind: "flow";
      readonly flow: SelfServiceFlow;
      readonly externalInput?: SelfServiceExternalInput;
    };

interface CarriedPatient {
  id: string;
  insurance?: Insurance;
}

interface SelfServiceShellState {
  isPinDialogOpen: boolean;
  carriedPatient: CarriedPatient | null;
  schedulerEnabled: boolean;
  prescriptionEnabled: boolean;
  welcomeMessage?: string;
  legalPracticeName?: string;
}

type SelfServiceShellAction =
  | { type: "set-pin-dialog-open"; open: boolean }
  | { type: "set-carried-patient"; carriedPatient: CarriedPatient | null }
  | {
      type: "apply-practice-settings";
      schedulerEnabled: boolean;
      prescriptionEnabled: boolean;
      welcomeMessage?: string;
      legalPracticeName?: string;
    };

const INITIAL_SELF_SERVICE_SHELL_STATE: SelfServiceShellState = {
  isPinDialogOpen: false,
  carriedPatient: null,
  schedulerEnabled: true,
  prescriptionEnabled: false,
};

const FLOW_INACTIVITY_TIMEOUT_MS = 60_000;

function selfServiceShellReducer(
  state: SelfServiceShellState,
  action: SelfServiceShellAction,
): SelfServiceShellState {
  switch (action.type) {
    case "set-pin-dialog-open":
      return { ...state, isPinDialogOpen: action.open };
    case "set-carried-patient":
      return { ...state, carriedPatient: action.carriedPatient };
    case "apply-practice-settings":
      return {
        ...state,
        schedulerEnabled: action.schedulerEnabled,
        prescriptionEnabled: action.prescriptionEnabled,
        welcomeMessage: action.welcomeMessage || state.welcomeMessage,
        legalPracticeName: action.legalPracticeName,
      };
  }
}

function shouldResetFlowForConnectivityLoss({
  activeFlow,
  isConnected,
  wasConnected,
}: {
  activeFlow: SelfServiceFlow | null;
  isConnected: boolean;
  wasConnected: boolean;
}) {
  return (
    wasConnected &&
    !isConnected &&
    activeFlow !== null &&
    activeFlow !== "emergency"
  );
}

interface DefaultHeaderActionsProps {
  sessionLanguage: string;
  onLanguageChange: (language: string) => void;
  onSettingsClick: () => void;
}

function DefaultHeaderActions({
  sessionLanguage,
  onLanguageChange,
  onSettingsClick,
}: DefaultHeaderActionsProps) {
  const { t } = useTranslation();

  return (
    <>
      <ReadabilityToggle />
      <AudioGuide />
      <AudioReplayButton />
      <LanguageSelector
        value={sessionLanguage}
        onLanguageChange={onLanguageChange}
      />
      <KioskClock />
      <Button
        data-testid="frontdesk-self-service-settings-btn"
        variant="ghost"
        size="icon"
        onClick={onSettingsClick}
        className="hidden sm:flex"
        title={t("self-service.settings")}
      >
        <Settings className="size-5" />
      </Button>
    </>
  );
}

interface SelfServiceHeaderProps {
  activeBranding: ActiveBrandingSnapshot;
  legalPracticeName?: string;
  viewTitle: ViewTitle | null;
  actions: ReactNode;
}

function SelfServiceHeader({
  activeBranding,
  legalPracticeName,
  viewTitle,
  actions,
}: SelfServiceHeaderProps) {
  return (
    /* Two stable rows. Row 1 holds the practice identity and the controls;
       the identity may use at most two lines and the controls collapse to
       icon-only buttons in narrow headers (container query), so a long
       practice name or a longer translation never pushes the controls onto
       extra lines. Row 2 holds the step title on its own, so it is never
       squeezed beside the brand. */
    <div className="@container/kiosk-header grid min-w-0 gap-y-3">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4">
        <PracticeIdentity
          active={activeBranding}
          practiceName={legalPracticeName}
        />
        <div className="flex flex-nowrap items-center justify-end gap-2">
          {actions}
        </div>
      </div>
      {viewTitle ? (
        <h1
          data-testid={viewTitle.testId}
          tabIndex={-1}
          className="m-0 min-w-0 font-heading text-2xl leading-tight font-bold tracking-[-0.02em] text-balance @3xl/kiosk-header:text-3xl"
        >
          {viewTitle.node}
        </h1>
      ) : null}
    </div>
  );
}

interface SelfServiceFooterProps {
  activeFlow: SelfServiceFlow | null;
  hasActiveFlow: boolean;
  isBackHidden: boolean;
  stepProgress: StepProgress | null;
  primaryAction: PublishedStepAction | null;
  secondaryAction: PublishedStepAction | null;
  onBack: () => void;
}

/* Fixed footer grid: back (left), progress (centre), step actions (right).
   The back cell keeps its width when the button is absent (KO-21), so the
   progress and the actions never jump. Below `md` the progress takes its own
   row above back and actions. */
function SelfServiceFooter({
  activeFlow,
  hasActiveFlow,
  isBackHidden,
  stepProgress,
  primaryAction,
  secondaryAction,
  onBack,
}: SelfServiceFooterProps) {
  const { t } = useTranslation();
  const showBack = (activeFlow || hasActiveFlow) && !isBackHidden;

  return (
    <div className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 md:grid-cols-[auto_minmax(0,1fr)_auto]">
      <div className="col-start-1 row-start-2 min-w-36 md:row-start-1">
        {showBack ? (
          <Button
            data-testid="frontdesk-self-service-back-btn"
            variant="ghost"
            size="2xl"
            className="px-6"
            onClick={onBack}
          >
            <ArrowLeft aria-hidden className="size-6" />
            {t("common.back")}
          </Button>
        ) : null}
      </div>

      <div className="col-span-2 row-start-1 min-w-0 md:col-span-1 md:col-start-2 md:mx-auto md:w-full md:max-w-[540px]">
        {stepProgress ? <StepProgressIndicator progress={stepProgress} /> : null}
      </div>

      <div className="col-start-2 row-start-2 min-w-0 justify-self-end md:col-start-3 md:row-start-1">
        <StepFooterActions primary={primaryAction} secondary={secondaryAction} />
      </div>

      {/* The reception-help button was a placeholder that only showed a toast;
          it is removed rather than shipped disabled. A real staff-notification
          feature is its own goal (see plans/frontdesk-release-review-fixes,
          D12). The `help-reception` strings stay: the reception-handoff
          compositions use them. */}
    </div>
  );
}

type SelfServiceAvailability = ReturnType<
  typeof resolveSelfServiceFlowAvailability
>;

interface SelfServiceFlowContentProps {
  activeFlow: SelfServiceFlow | null;
  availability: SelfServiceAvailability;
  localEmergencyAvailable: boolean;
  carriedPatient: CarriedPatient | null;
  welcomeMessage?: string;
  onSelectFlow: (flow: SelfServiceFlow) => void;
  onFlowComplete: (
    nextFlow?: SelfServiceFlow,
    carried?: CarriedPatient,
  ) => void;
}

function SelfServiceFlowContent({
  activeFlow,
  availability,
  localEmergencyAvailable,
  carriedPatient,
  welcomeMessage,
  onSelectFlow,
  onFlowComplete,
}: SelfServiceFlowContentProps) {
  const { adminDefaultLanguage } = useAccessibilitySession();
  if (!activeFlow) {
    const StartComposition = localEmergencyAvailable
      ? StartWithLocalEmergency
      : Start;
    return (
      <StartComposition
        onSelectFlow={onSelectFlow}
        isConnected={availability.isConnected}
        schedulerAvailable={availability.schedulerAvailable}
        prescriptionAvailable={availability.prescriptionAvailable}
        welcomeMessage={welcomeMessage}
        welcomeMessageLanguage={adminDefaultLanguage}
      />
    );
  }

  switch (activeFlow) {
    case "checkin":
      return (
        <CheckInFlow
          onComplete={onFlowComplete}
          schedulerAvailable={availability.schedulerAvailable}
        />
      );

    case "scheduler":
      return (
        <SchedulerFlow
          onComplete={onFlowComplete}
          initialPatient={carriedPatient ?? undefined}
        />
      );

    case "prescription":
      return <PrescriptionFlow onComplete={onFlowComplete} />;

    case "emergency":
      return <EmergencyFlow onComplete={onFlowComplete} />;
  }
}

/**
 * Inner component that uses the SelfService context and accessibility session
 */
function SelfServiceContent({
  activeFlow,
  setActiveFlow,
  onNavigateToSettings,
  requirePinVerificationForSettings,
  headerActions,
  readinessOverride,
  onFlowStart,
  onExperienceContextChange,
  bridgeConnected,
  languageEntryMode,
}: {
  activeFlow: SelfServiceFlow | null;
  setActiveFlow: (flow: SelfServiceFlow | null) => void;
  onNavigateToSettings?: () => void;
  requirePinVerificationForSettings: boolean;
  headerActions?: ReactNode;
  readinessOverride?: SelfServiceReadinessOverride | null;
  onFlowStart?: (flow: SelfServiceFlow) => void | Promise<void>;
  onExperienceContextChange?: (
    context: SelfServiceExperienceContext,
  ) => void | Promise<void>;
  bridgeConnected: boolean;
  languageEntryMode: "loading" | "enabled" | "disabled";
}) {
  const platform = usePlatform();
  const settingsUnlock = useOptionalSettingsUnlock();
  const kioskPolicy = useKioskPolicy();
  const localEmergencyAvailable = isLocalEmergencyAvailable(kioskPolicy);
  const branding = useOptionalBranding();
  const activeBranding = branding?.active ?? DEFAULT_ACTIVE_BRANDING;
  const {
    handleBack,
    hasActiveFlow,
    isBackHidden,
    stepProgress,
    viewTitle,
    primaryAction,
    secondaryAction,
    externalInput,
    setExternalInput,
    cardReadPending,
    terminalSessionLeaseActive,
  } = useSelfService();
  const {
    readabilityMode,
    triggerReset,
    resetSession,
    sessionLanguage,
    setSessionLanguage,
    presentationSessionStarted,
  } = useAccessibilitySession();
  const [shellState, dispatchShell] = useReducer(
    selfServiceShellReducer,
    INITIAL_SELF_SERVICE_SHELL_STATE,
  );
  const [isSettingsNavigationPending, setIsSettingsNavigationPending] =
    useState(false);
  const {
    isPinDialogOpen,
    carriedPatient,
    schedulerEnabled,
    prescriptionEnabled,
    welcomeMessage,
    legalPracticeName,
  } = shellState;
  const availability = resolveSelfServiceFlowAvailability({
    bridgeConnected,
    schedulerEnabled,
    prescriptionEnabled,
    readinessOverride,
  });
  const prevConnectedRef = useRef(availability.isConnected);
  const lastExperienceContextRef = useRef<string | null>(null);
  const experienceContext = useMemo<SelfServiceExperienceContext>(() => {
    if (activeFlow === null) return { kind: "hub" };
    if (externalInput === null) return { kind: "flow", flow: activeFlow };
    return { kind: "flow", flow: activeFlow, externalInput };
  }, [activeFlow, externalInput]);

  const notifyFlowStart = useCallback(
    function notifyFlowStart(flow: SelfServiceFlow) {
      if (onFlowStart === undefined) return;

      try {
        void Promise.resolve(onFlowStart(flow)).catch((error: unknown) =>
          reportOperationalError({
            action: "self-service.flow-start-observer",
            severity: "warning",
            source: "explicit",
            component: "SelfServicePage",
            error,
          }),
        );
      } catch (error) {
        void reportOperationalError({
          action: "self-service.flow-start-observer",
          severity: "warning",
          source: "explicit",
          component: "SelfServicePage",
          error,
        });
      }
    },
    [onFlowStart],
  );

  useEffect(
    function publishExperienceContext() {
      if (onExperienceContextChange === undefined) return;
      const signature =
        experienceContext.kind === "hub"
          ? "hub"
          : `${experienceContext.flow}:${experienceContext.externalInput ?? "none"}`;
      if (lastExperienceContextRef.current === signature) return;
      lastExperienceContextRef.current = signature;

      try {
        void Promise.resolve(
          onExperienceContextChange(experienceContext),
        ).catch((error: unknown) =>
          reportOperationalError({
            action: "self-service.experience-context-observer",
            severity: "warning",
            source: "explicit",
            component: "SelfServicePage",
            error,
          }),
        );
      } catch (error) {
        void reportOperationalError({
          action: "self-service.experience-context-observer",
          severity: "warning",
          source: "explicit",
          component: "SelfServicePage",
          error,
        });
      }
    },
    [experienceContext, onExperienceContextChange],
  );

  // Route through reassurance overlay when disconnected during active non-emergency flow
  useEffect(
    function connectivity() {
      const wasConnected = prevConnectedRef.current;
      prevConnectedRef.current = availability.isConnected;
      const shouldResetFlow = shouldResetFlowForConnectivityLoss({
        activeFlow,
        isConnected: availability.isConnected,
        wasConnected,
      });

      if (shouldResetFlow && !terminalSessionLeaseActive) {
        frontdeskDiagnosticLogger.warn(
          "[FrontDesk+] Active self-service flow interrupted because bridge connectivity was lost",
          {
            activeFlow,
          },
        );
        triggerReset("offline");
      }
    },
    [availability.isConnected, activeFlow, terminalSessionLeaseActive, triggerReset],
  );

  useEffect(
    function revokeUnauthorizedLocalEmergency() {
      if (activeFlow !== "emergency" || localEmergencyAvailable) return;

      setExternalInput(null);
      resetSession();
      setActiveFlow(null);
    },
    [
      activeFlow,
      localEmergencyAvailable,
      resetSession,
      setActiveFlow,
      setExternalInput,
    ],
  );

  // Load practice settings to check feature availability
  useEffect(
    function runLoadSettingsEffect() {
      const loadSettings = async () => {
        try {
          const practice = await platform.getPracticeSettings();
          const nextSchedulerAvailable =
            practice.schedulerAvailableInSelfService ?? true;
          const nextPrescriptionAvailable =
            FEATURE_FLAGS.prescriptionService &&
            (practice.prescriptionServiceAvailableInSelfService ?? false);

          dispatchShell({
            type: "apply-practice-settings",
            schedulerEnabled: nextSchedulerAvailable,
            prescriptionEnabled: nextPrescriptionAvailable,
            welcomeMessage: practice.welcomeMessage,
            legalPracticeName: practice.practiceName,
          });

          frontdeskDiagnosticLogger.info(
            "[FrontDesk+] Loaded self-service practice settings",
            {
              schedulerAvailableInSelfService: nextSchedulerAvailable,
              prescriptionServiceAvailableInSelfService:
                nextPrescriptionAvailable,
              welcomeMessageConfigured: Boolean(practice.welcomeMessage),
              hasSelectedTerminalId: Boolean(practice.selectedTerminalId),
              hasDeviceName: Boolean(practice.deviceName),
            },
          );
        } catch (error) {
          void reportOperationalError({
            action: "self-service.settings-load",
            severity: "error",
            source: "native",
            component: "SelfServicePage",
            error,
          });
        }
      };
      loadSettings();
    },
    [platform],
  );

  // Listen for exit fullscreen request (keyboard shortcut)
  useEffect(function syncIsPinDialogOpen() {
    const handleRequestExit = () => {
      dispatchShell({ type: "set-pin-dialog-open", open: true });
    };
    window.addEventListener("request-exit-fullscreen", handleRequestExit);
    return function unbindEventListeners() {
      return window.removeEventListener(
        "request-exit-fullscreen",
        handleRequestExit,
      );
    };
  }, []);

  const handleSelectFlow = (flow: SelfServiceFlow) => {
    if (flow === "emergency" && !localEmergencyAvailable) return;

    // Manual hub entry is always a fresh patient — never reuse a carried identity.
    dispatchShell({ type: "set-carried-patient", carriedPatient: null });
    setExternalInput(null);
    setActiveFlow(flow);
    notifyFlowStart(flow);
  };

  const handleFlowComplete = useCallback(
    (nextFlow?: SelfServiceFlow, carried?: CarriedPatient) => {
      if (nextFlow) {
        // Carry an identified patient into the next flow (e.g. check-in →
        // scheduler after "no appointment found"). Undefined for normal redirects.
        dispatchShell({
          type: "set-carried-patient",
          carriedPatient: carried ?? null,
        });
        setExternalInput(null);
        setActiveFlow(nextFlow);
        notifyFlowStart(nextFlow);
      } else {
        // Session ending — reset session overrides and return to start
        dispatchShell({ type: "set-carried-patient", carriedPatient: null });
        setExternalInput(null);
        resetSession();
        setActiveFlow(null);
      }
    },
    [notifyFlowStart, resetSession, setActiveFlow, setExternalInput],
  );

  const handleInactiveFlowTimeout = useCallback(
    function handleInactiveFlowTimeout() {
      dispatchShell({ type: "set-carried-patient", carriedPatient: null });
      setExternalInput(null);
      resetSession();
      setActiveFlow(null);
    },
    [resetSession, setActiveFlow, setExternalInput],
  );

  useEffect(
    function resetActiveFlowAfterInactivity() {
      if ((!activeFlow && !presentationSessionStarted) || activeFlow === "emergency" || cardReadPending || terminalSessionLeaseActive) return;

      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const interactionEvents = [
        "pointerdown",
        "keydown",
        "touchstart",
        "wheel",
      ] as const;

      const resetInactivityTimer = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(
          handleInactiveFlowTimeout,
          FLOW_INACTIVITY_TIMEOUT_MS,
        );
      };

      resetInactivityTimer();

      for (const eventName of interactionEvents) {
        window.addEventListener(eventName, resetInactivityTimer, true);
      }

      return function stopInactivityTimer() {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        for (const eventName of interactionEvents) {
          window.removeEventListener(eventName, resetInactivityTimer, true);
        }
      };
    },
    [activeFlow, presentationSessionStarted, cardReadPending, handleInactiveFlowTimeout, terminalSessionLeaseActive],
  );

  useEffect(
    function offerPinAfterTurnedAwaySettingsVisit() {
      if (!settingsUnlock?.isPinRequested) return;
      settingsUnlock.clearPinRequest();
      dispatchShell({ type: "set-pin-dialog-open", open: true });
    },
    [settingsUnlock],
  );

  useEffect(
    function navigateAfterSettingsUnlockCommits() {
      if (!isSettingsNavigationPending || !settingsUnlock?.isUnlocked) return;

      setIsSettingsNavigationPending(false);
      onNavigateToSettings?.();
    }, [
      isSettingsNavigationPending,
      onNavigateToSettings,
      settingsUnlock?.isUnlocked,
    ]
  );

  // Asked once when the hub loads, which is before any patient can reach
  // check-in, so the flow has a real answer instead of a build-time guess.
  useEffect(function refreshEebAvailabilityOnHubLoad() {
    void refreshEebAvailability();
  }, []);

  const handleSettingsClick = async () => {
    if (!requirePinVerificationForSettings) {
      onNavigateToSettings?.();
      return;
    }

    // A device with no usable PIN cannot be unlocked by the forbidden default;
    // it goes straight into settings so the admin must set a real PIN there
    // (the settings schema rejects `0000`). ITSiRL §75b SGB V.
    // The settings guard admits that visitor itself — see
    // `SettingsPage.admitUnconfiguredDeviceForSetup`, which owns the rule for
    // every entry path, including the Electron boot redirect that cannot reach
    // the unlock session.
    const configured = await platform.isPinConfigured();
    if (!configured) {
      onNavigateToSettings?.();
      return;
    }

    dispatchShell({ type: "set-pin-dialog-open", open: true });
  };

  const handlePinVerified = () => {
    dispatchShell({ type: "set-pin-dialog-open", open: false });
    if (settingsUnlock === undefined) {
      onNavigateToSettings?.();
      return;
    }

    // React does not commit the provider state synchronously inside this
    // callback. Navigating in the same event mounted the guarded page with the
    // previous `isUnlocked=false` snapshot, which immediately bounced the
    // verified admin back to the hub. The effect above observes the committed
    // state before crossing the route boundary.
    setIsSettingsNavigationPending(true);
    settingsUnlock.unlock();
  };

  const handlePinDialogClose = (open: boolean) => {
    dispatchShell({ type: "set-pin-dialog-open", open });
  };

  const renderFooter = useCallback(
    function renderFooter() {
      return (
        <SelfServiceFooter
          activeFlow={activeFlow}
          hasActiveFlow={hasActiveFlow}
          isBackHidden={isBackHidden}
          stepProgress={stepProgress}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          onBack={handleBack}
        />
      );
    },
    [
      activeFlow,
      handleBack,
      hasActiveFlow,
      isBackHidden,
      primaryAction,
      secondaryAction,
      stepProgress,
    ],
  );

  const resolvedHeaderActions = headerActions ?? (
    <DefaultHeaderActions
      sessionLanguage={sessionLanguage}
      onLanguageChange={setSessionLanguage}
      onSettingsClick={handleSettingsClick}
    />
  );

  return (
    <ReadabilityProvider value={readabilityMode}>
      <PracticeBrandScope active={activeBranding} className="h-full">
        <div
          className={cn(
            "frontdesk-kiosk-shell relative flex flex-col h-full bg-background",
            readabilityMode && "frontdesk-readability-mode",
          )}
        >
          {!availability.isConnected &&
            (localEmergencyAvailable ? (
              <BridgeOfflineBannerWithLocalEmergency />
            ) : (
              <BridgeOfflineBanner />
            ))}
          <ScrollContainer
            className="relative z-[1] flex-1"
            header={
              <SelfServiceHeader
                activeBranding={activeBranding}
                legalPracticeName={legalPracticeName}
                viewTitle={viewTitle}
                actions={resolvedHeaderActions}
              />
            }
            footer={renderFooter}
          >
            {activeFlow === null && languageEntryMode === "loading" ? (
              <SessionEntryLoading>
                {localEmergencyAvailable ? <LocalEmergencyTile onSelect={() => handleSelectFlow("emergency")} /> : null}
              </SessionEntryLoading>
            ) : activeFlow === null && languageEntryMode === "enabled" && !presentationSessionStarted ? (
              localEmergencyAvailable
                ? <LanguageEntryWithLocalEmergency onEmergency={() => handleSelectFlow("emergency")} />
                : <LanguageEntry />
            ) : (
            <SelfServiceFlowContent
              activeFlow={activeFlow}
              availability={availability}
              localEmergencyAvailable={localEmergencyAvailable}
              carriedPatient={carriedPatient}
              welcomeMessage={welcomeMessage}
              onSelectFlow={handleSelectFlow}
              onFlowComplete={handleFlowComplete}
            />
            )}
          </ScrollContainer>
          <StepAnnouncer viewTitle={viewTitle} />
        </div>
      </PracticeBrandScope>
      <ResetReassuranceOverlay />
      <PinVerificationDialog
        open={isPinDialogOpen}
        onOpenChange={handlePinDialogClose}
        onVerified={handlePinVerified}
        verifyPin={platform.verifyPin}
      />
    </ReadabilityProvider>
  );
}

/**
 * Main self-service page that orchestrates flow selection and execution.
 * Wraps content in SelfServiceProvider + AccessibilitySessionProvider.
 */
export function SelfServicePage({
  onNavigateToSettings,
  requirePinVerificationForSettings = true,
  headerActions,
  readinessOverride,
  onFlowStart,
  onExperienceContextChange,
}: SelfServicePageProps) {
  const [activeFlow, setActiveFlow] = useState<SelfServiceFlow | null>(null);
  const [adminDefaultLanguage, setAdminDefaultLanguage] = useState("de");
  const [languageEntryMode, setLanguageEntryMode] = useState<"loading" | "enabled" | "disabled">("loading");
  const platform = usePlatform();
  const bridgeConnectivity = useBridgeConnectivity();

  // Load admin default language from settings
  useEffect(
    function syncAdminDefaultLanguage() {
      void (async () => {
        try {
          const styling = await platform.getStylingSettings();
          setLanguageEntryMode(styling.languageSelectionOnSessionStart === true ? "enabled" : "disabled");
          if (styling.defaultKioskLanguage) {
            setAdminDefaultLanguage(styling.defaultKioskLanguage);
          } else if (styling.language) {
            setAdminDefaultLanguage(styling.language);
          }
        } catch (error) {
          void reportOperationalError({
            action: "self-service.language-load",
            severity: "warning",
            source: "native",
            component: "SelfServicePage",
            error,
          });
          setLanguageEntryMode("disabled");
        }
      })();
    },
    [platform],
  );

  // Called when back is pressed and no flow has registered a handler
  const handleExitFlow = useCallback(() => {
    setActiveFlow(null);
  }, []);

  // Called when reset/reassurance overlay completes
  const handleResetComplete = useCallback(() => {
    setActiveFlow(null);
  }, []);

  return (
    <AccessibilitySessionProvider
      adminDefaultLanguage={adminDefaultLanguage}
      onResetComplete={handleResetComplete}
    >
      <SelfServiceProvider onExitFlow={handleExitFlow}>
        <KioskPolicyProvider
          bridgeConnected={bridgeConnectivity.isConnected}
          bridgeCheckComplete={bridgeConnectivity.lastChecked !== null}
          refreshKey={bridgeConnectivity.lastChecked?.getTime()}
        >
          <SelfServiceContent
            activeFlow={activeFlow}
            setActiveFlow={setActiveFlow}
            onNavigateToSettings={onNavigateToSettings}
            requirePinVerificationForSettings={
              requirePinVerificationForSettings
            }
            headerActions={headerActions}
            readinessOverride={readinessOverride}
            onFlowStart={onFlowStart}
            onExperienceContextChange={onExperienceContextChange}
            bridgeConnected={bridgeConnectivity.isConnected}
            languageEntryMode={languageEntryMode}
          />
        </KioskPolicyProvider>
      </SelfServiceProvider>
    </AccessibilitySessionProvider>
  );
}
